import { Rating, Stack, Typography, useTheme } from "@mui/material";
import type { IconContainerProps } from "@mui/material/Rating";
import { alpha } from "@mui/material/styles";
import { type FC, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { AnimatedNumber } from "../../../../components/UIToolkit/Animations/AnimatedNumber";
import { FONT_SIZE } from "../../../../theme/tokens";
import { goldAccent, heroAccent, heroInk, heroInkMuted, heroTrack } from "../colors";
import { PREDICTION_MULTICOL_QUERY } from "../predictionLayout.consts";

/** The goal star is an outline, not a fill — dimmed off full gold so it reads as "not yet earned". */
const GOAL_STAR_OPACITY = 0.6;

type Props = {
	// The current star rating (gold, glowing) — the hero's focal headline now the gauge is gone.
	currentStars: number;
	// The target star rating — marked ON the star scale (gold-outlined star) rather than spelled
	// out, so the lead block shows what it's aiming at without repeating the gap story's copy.
	targetStars: number;
	// Current PBJ points, rounded — the big focal number (AnimatedNumber-wrapped).
	currentPointsRounded: number;
	// True when an Action Plan lifts the score beyond current — shows the "→ projected" line.
	hasActivePlan: boolean;
	// The plan-projected points, rounded (AnimatedNumber-wrapped).
	withPlanPoints: number;
	// The gauge scale max — rendered as "/ {max} pts".
	maxPoints: number;
};

/**
 * The hero's LEAD block (replaces the radial gauge): the current star rating as the big
 * gold focal headline, the current-points number "{now} / {max} pts", and an optional
 * "→ {withPlan} with plan" projection line. The points→target arc the gauge used to draw is
 * now carried entirely by the gap story's linear meter, so this block is a compact,
 * left-aligned identity (centered when the hero stacks). Pure presentation on the dark stage.
 *
 * The GOAL is marked on the star scale itself — the target star wears a gold outline, the same
 * "gold = target" language the progress meter's gold tick uses — so the row reads "at 2, aiming
 * at 4" without adding words that the gap story already says.
 */
export const HeroScoreLead: FC<Props> = ({ currentStars, targetStars, currentPointsRounded, hasActivePlan, withPlanPoints, maxPoints }) => {
	const { t } = useTranslation();
	const theme = useTheme();
	const gold = goldAccent(theme);

	// Flags the target star so the sx below can single it out. Only meaningful while the goal is
	// still unmet — once reached, that star is filled gold and needs no marker.
	const goalStar = targetStars > currentStars ? targetStars : null;
	// `ownerState` is dropped deliberately: MUI's slot machinery forwards it to a custom icon slot,
	// and React errors on it reaching the DOM as an unknown attribute.
	const starContainer = useCallback(
		({ value, ownerState: _ownerState, ...props }: IconContainerProps & { ownerState?: unknown }) => (
			<span {...props} data-goal-star={value === goalStar ? "" : undefined} />
		),
		[goalStar],
	);

	return (
		<Stack
			spacing={0.625}
			sx={{
				alignItems: "center",
				textAlign: "center",
				height: "100%",
				justifyContent: "center",
				[PREDICTION_MULTICOL_QUERY]: { alignItems: "flex-start", textAlign: "left" },
			}}
			data-testid="pbj-model-hero-score-lead"
		>
			<Typography sx={{ textTransform: "uppercase", letterSpacing: "0.16em", fontWeight: 700, fontSize: FONT_SIZE.LABEL_MICRO, color: heroInkMuted() }}>
				{t("pbj_model.gauge.projected_eyebrow", { count: currentStars })}
			</Typography>
			{/* The star rating is THE headline of this page — enlarged to be the focal anchor of the
			    left zone (gold with a stronger glow at this size). Sized just under the display
			    score number so stars + points read as one confident score block, not a caption. */}
			<Rating
				value={currentStars}
				readOnly
				slots={{ icon: starContainer }}
				aria-label={t("pbj_model.gauge.stars_aria", { now: currentStars, goal: targetStars })}
				sx={{
					fontSize: "2.75rem",
					"& .MuiRating-iconFilled": { color: gold, filter: `drop-shadow(0 0 16px ${alpha(gold, 0.55)})` },
					"& .MuiRating-iconEmpty": { color: heroTrack() },
					"& .MuiRating-iconEmpty[data-goal-star]": { color: alpha(gold, GOAL_STAR_OPACITY) },
				}}
				data-testid="pbj-model-hero-stars"
			/>
			<Stack direction="row" spacing={0.75} sx={{ alignItems: "baseline", mt: 0.25 }}>
				<Typography
					component="div"
					sx={{
						fontSize: FONT_SIZE.DISPLAY_LG,
						fontWeight: 700,
						lineHeight: 0.9,
						letterSpacing: "-0.035em",
						fontVariantNumeric: "tabular-nums",
						color: heroInk(),
					}}
					data-testid="pbj-model-hero-score"
				>
					<AnimatedNumber value={currentPointsRounded} />
				</Typography>
				<Typography component="span" sx={{ fontSize: FONT_SIZE.TITLE_MD, fontWeight: 500, color: heroInkMuted(), fontVariantNumeric: "tabular-nums" }}>
					{t("pbj_model.gauge.of_max_points_short", { max: maxPoints })}
				</Typography>
			</Stack>
			{/* With-plan projection — only shown when a plan actually lifts the score. The "→" +
			    accent number echo the gap story's projected meter fill. */}
			{hasActivePlan && (
				<Stack direction="row" spacing={0.5} sx={{ mt: 0.25, alignItems: "center" }} data-testid="pbj-model-hero-score-projected">
					<Typography component="span" sx={{ fontSize: FONT_SIZE.TITLE_SM, fontWeight: 700, color: heroAccent(theme), lineHeight: 1 }}>
						→
					</Typography>
					<Typography
						component="span"
						sx={{ fontSize: FONT_SIZE.TITLE_SM, fontWeight: 700, color: heroAccent(theme), fontVariantNumeric: "tabular-nums", lineHeight: 1 }}
					>
						<AnimatedNumber value={withPlanPoints} />
					</Typography>
					<Typography component="span" sx={{ fontSize: FONT_SIZE.SECONDARY, fontWeight: 500, color: heroInkMuted(), lineHeight: 1 }}>
						{t("pbj_model.gauge.with_plan")}
					</Typography>
				</Stack>
			)}
		</Stack>
	);
};
